import { FlowApiClient } from "@/features/flow/api-client/api-client.ts";
import { Routes } from "@/features/router";
import { useUser } from "@/features/user/hooks/use-user";
import { useAppDispatch } from "@/redux/hooks";
import { setExpirationToken, setUser } from "@/redux/slices/userSlice";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setJwt } from "../utils";
import { OlkyPassRoutes } from "@/utils/routes";

type CachedUser = {
  email?: string;
  name?: string;
  avatar?: string;
};

const getCachedUser = () => JSON.parse(sessionStorage.getItem("user") || "{}") as CachedUser;

const setCachedUser = (user: CachedUser) => sessionStorage.setItem("user", JSON.stringify(user));

export const useAuth = () => {
  const navigate = useNavigate();
  const { useUserProfile } = useUser();
  const user = useUserProfile();
  const cached = useQuery<CachedUser>({ queryKey: ["cached-user"], queryFn: getCachedUser });
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();
  const logout = async () => {
    setJwt({ token: null });
    dispatch(setExpirationToken(null));
    dispatch(setUser(null));
    queryClient.clear();
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("jwt");
    if (
      localStorage.getItem("user") ||
      localStorage.getItem("username") ||
      localStorage.getItem("jwt")
    ) {
      localStorage.removeItem("user");
      localStorage.removeItem("username");
      localStorage.removeItem("jwt");
    }
    document.location = "/";
  };

  const refresh = async (redirect?: boolean) => {
    await user.refetch();
    if (redirect) {
      try {
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get("r");
        const redirectToLvl = searchParams.get("level_to_complete");

        const redirectDashbaord = redirectToLvl
          ? `${OlkyPassRoutes.level}?level_to_complete=${redirectToLvl}`
          : Routes.dashboard;
        const route = (redirect && decodeURIComponent(redirect)) || redirectDashbaord || "";
        navigate(route);
      } catch (_) {
        document.location = "/";
      }
    }
  };

  const createMirrorIdentity = async (partner_uuid: string) => {
    try {
      await FlowApiClient.createMirrorFromReal(partner_uuid);
    } catch (_) {
      console.log("create mirror issue");
    }
  };
  return {
    logout,
    cached: cached.data,
    removeCached: async () => {
      setCachedUser({});
      cached.refetch();
      if (user.data) {
        await logout();
      }
    },
    user,
    refresh,
    createMirrorIdentity
  };
};
